import { LayoutAdjustmentStrategyFactory } from './layoutStrategyFactory';
import * as store from '../store';
import {
  getOverlappedSiblings,
  getParentContainers,
  getSiblingContainers,
  getDraggedLayout,
  areAllComponentsMobileOnly,
} from '../utils/layoutUtils';
import { getIsMobileBlockReparentingEnabled } from '../utils/common';
import { createDraggedComponentOpacityHandler } from '../utils/draggedComponentOpacityUtils';
import { showNotificationOnCollision } from '../utils/showNotificationUtils';
import type { BaseDragPlugin } from '#packages/rEditor';
import type { LayoutAdjustmentStrategy, PluginScope } from './types';
import type { CompLayout } from '@wix/document-services-types';

const applyHighlightToRestrictedZones = (scope: PluginScope) => {
  const {
    editorAPI,
    selectedComp,
    siblingContainers,
    applyDraggedComponentOpacity,
  } = scope;

  if (areAllComponentsMobileOnly(editorAPI, selectedComp)) {
    return;
  }

  const draggedLayout = getDraggedLayout(editorAPI, selectedComp);
  const restrictedZones = getOverlappedSiblings(
    editorAPI,
    selectedComp,
    siblingContainers,
  );

  store.setDraggedLayout(draggedLayout);
  store.setRestrictedZones(restrictedZones);

  applyDraggedComponentOpacity(restrictedZones.length > 0);
};

const onDragStart = (
  scope: PluginScope,
  layoutStrategy: LayoutAdjustmentStrategy,
) => {
  layoutStrategy.initialize(scope);
};

const onEndDrag = async (
  scope: PluginScope,
  layoutStrategy: LayoutAdjustmentStrategy,
) => {
  store.setRestrictedZones([]);

  layoutStrategy.finalize(scope);
};

const onOverrideLayout = (
  scope: PluginScope,
  layoutStrategy: LayoutAdjustmentStrategy,
  layout: CompLayout,
): CompLayout => {
  applyHighlightToRestrictedZones(scope);

  const layoutOptions = {
    onCollision: () => showNotificationOnCollision(scope),
  };

  return layoutStrategy.adjustLayout(scope, layout, layoutOptions);
};

/**
 * Prevents reparenting on mobile devices. When a user attempts to reparent a component (i.e., attach it to another container), this plugin blocks the area where the component can be dragged.
 */
export const createMobileReparentingBlockPlugin = (): BaseDragPlugin => ({
  init: ({ pluginFactoryScope, hooks }) => {
    const { editorAPI, selectedComp } = pluginFactoryScope;

    if (!getIsMobileBlockReparentingEnabled(editorAPI)) {
      return;
    }

    const parentContainers = getParentContainers(editorAPI, selectedComp);
    const siblingContainers = getSiblingContainers(editorAPI, selectedComp);

    const applyDraggedComponentOpacity = createDraggedComponentOpacityHandler(
      editorAPI,
      selectedComp,
    );

    const scope: PluginScope = {
      editorAPI,
      selectedComp,
      siblingContainers,
      parentContainers,
      applyDraggedComponentOpacity,
    };

    const strategyFactory = new LayoutAdjustmentStrategyFactory(scope);
    const layoutStrategy = strategyFactory.getStrategy();

    hooks.onDragStart.tap(() => onDragStart(scope, layoutStrategy));
    hooks.overrideLayout.tap((l) => onOverrideLayout(scope, layoutStrategy, l));
    hooks.endDrag.tap(() => onEndDrag(scope, layoutStrategy));
  },
});
